import { Box, Button, Chip, InputAdornment, MenuItem, TextField } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import PropTypes from 'prop-types'
import { AUDIENCES, BLUE, FILTER_STATUSES, TYPES } from '../utils'

const ALL = ''

/** Selected chip fills with the panel blue; the rest stay as light blue outlines. */
const chipSx = (selected) => ({
  fontWeight: 600,
  cursor: 'pointer',
  backgroundColor: selected ? BLUE.main : '#fff',
  color: selected ? '#fff' : BLUE.main,
  border: `1px solid ${selected ? BLUE.main : BLUE.line}`,
  '&:hover': { backgroundColor: selected ? BLUE.dark : BLUE.soft },
})

/**
 * Status is a chip row (it's the thing admins filter by constantly) while the
 * narrower type/audience/search filters sit in a single line beneath it.
 */
export default function FilterBar({ filters, onChange, onRefresh, total }) {
  const setFilter = (field) => (value) => onChange({ ...filters, [field]: value })

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={2}>
        <Chip
          label="All"
          size="small"
          onClick={() => setFilter('status')(ALL)}
          sx={chipSx(filters.status === ALL)}
        />
        {FILTER_STATUSES.map((status) => (
          <Chip
            key={status.value}
            label={status.label}
            size="small"
            onClick={() => setFilter('status')(status.value)}
            sx={chipSx(filters.status === status.value)}
          />
        ))}

        <Box flexGrow={1} />

        <Chip
          label={`${total} total`}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: BLUE.soft,
            color: BLUE.main,
          }}
        />
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <TextField
          size="small"
          placeholder="Search name, title or message"
          value={filters.search}
          onChange={(e) => setFilter('search')(e.target.value)}
          sx={{ minWidth: 260, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#9aa0a6' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Type"
          value={filters.type}
          onChange={(e) => setFilter('type')(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value={ALL}>All types</MenuItem>
          {TYPES.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Audience"
          value={filters.audience}
          onChange={(e) => setFilter('audience')(e.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value={ALL}>All audiences</MenuItem>
          {AUDIENCES.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>

        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          sx={{ textTransform: 'none', color: BLUE.main }}
        >
          Refresh
        </Button>

        <Button
          size="small"
          onClick={() => onChange({ status: ALL, type: ALL, audience: ALL, search: '' })}
          sx={{ textTransform: 'none', color: '#777' }}
        >
          Clear filters
        </Button>
      </Box>
    </Box>
  )
}

FilterBar.propTypes = {
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  total: PropTypes.number,
}

FilterBar.defaultProps = { total: 0 }
